import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { getFromLocalStorage, setIntoLocalStorage } from 'Internals/local-storage';
import { sortWithSortable } from 'Internals/sort';
import { CardListSortSelector } from './SortSelector.view';
import { TestID } from 'src/testID';

import './CardList.css';

const LOCAL_STORAGE_KEY = 'sesam--card-list';
const SORT_BY_KEY = 'sortBy';
const SORT_ASC_KEY = 'sortAsc';

const getFieldData = (obj, field) => {
  if (typeof field.data === 'function') {
    const result = field.data(obj, field);
    return typeof result === 'undefined' ? '' : result;
  }
  return get(obj, field.data, '');
};

const defaultFieldSorter = (field, row1, row2) => {
  const row1Data = getFieldData(row1, field);
  const row2Data = getFieldData(row2, field);

  if (row1Data && !row2Data) return -1;
  if (row2Data && !row1Data) return 1;

  if (typeof row1Data === 'number' && typeof row2Data === 'number') {
    return row1Data < row2Data ? -1 : 1;
  }

  return row1Data.toString().trim().toLowerCase() < row2Data.toString().trim().toLowerCase()
    ? -1
    : 1;
};

/**
 * Retrieve personalized sort direction from local storage
 */
const loadPersonalSortAsc = (id) =>
  getFromLocalStorage(LOCAL_STORAGE_KEY, [id, SORT_ASC_KEY], true);

/**
 * Retrieve personalized sort column from local storage
 */
const loadPersonalSortBy = (id, defaultSortByKey) =>
  getFromLocalStorage(LOCAL_STORAGE_KEY, [id, SORT_BY_KEY], defaultSortByKey);

const Cards = (props) => (
  <>
    {props.cards.map((card) => {
      const CardRenderer = card.renderer;

      return (
        <li key={card.id}>
          <CardRenderer {...card} />
        </li>
      );
    })}
  </>
);

export const CardList = ({
  id,
  defaultSortByKey,
  pinnedIds,
  data,
  dataKey,
  header,
  sortable,
  fields,
}) => {
  const [sortAscending, setSortAscending] = useState(loadPersonalSortAsc(id));
  const [sortBy, setSortBy] = useState(loadPersonalSortBy(id, defaultSortByKey));

  /**
   * Stores the sort field (and direction) for this list in localStorage
   * @param {Field} sortByField The field object to save
   */
  const setSortByState = (sortByField) => {
    const sortCardsBy = sortByField.header;
    const sortCardsAscending = sortBy === sortCardsBy ? !sortAscending : true;

    setSortBy(sortCardsBy);
    setSortAscending(sortCardsAscending);
    setIntoLocalStorage(LOCAL_STORAGE_KEY, [id, SORT_BY_KEY], sortCardsBy);
    setIntoLocalStorage(LOCAL_STORAGE_KEY, [id, SORT_ASC_KEY], sortCardsAscending);
  };

  const getSortedCards = (pinned = false) => {
    const cards = data.filter((dataRow) => {
      const rowKey = get(dataRow, dataKey);
      const isPinned = pinnedIds.includes(rowKey);

      return pinned ? isPinned : !isPinned;
    });

    // No sense in sorting pinned items
    if (pinned) {
      return cards;
    }

    // Find the field object that we are sorting by
    const sortField = fields.find((field) => field.header === sortBy);

    if (sortField) {
      // If no sorter defined (and not explicitly set to null), use the default sorter
      Object.assign(sortField, {
        sorter: sortField.sorter === undefined ? defaultFieldSorter : sortField.sorter,
      });
      return sortWithSortable(cards, sortField, sortAscending);
    }

    return cards;
  };

  const hasHeader = header || sortable;
  const sortableFields = fields.filter((field) => field.sorter !== null);
  const pinnedCards = getSortedCards(true);
  const otherCards = getSortedCards();

  return (
    <div className="card-list">
      {hasHeader && (
        <div className="card-list__header">
          {header && <div className="card-list__header-controls">{header}</div>}
          {sortableFields.length > 0 && (
            <CardListSortSelector
              currentSortBy={sortBy}
              currentSortAsc={sortAscending}
              fields={sortableFields}
              onChange={setSortByState}
            />
          )}
        </div>
      )}
      <ul className="card-list__list" data-testid={TestID.CardList}>
        <Cards cards={pinnedCards} />
        <Cards cards={otherCards} />
      </ul>
    </div>
  );
};

CardList.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      renderer: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    })
  ).isRequired,

  /** Name of property in `props.data` objects that provides a unique key; accepts lodash `get` selector strings */
  dataKey: PropTypes.string.isRequired,

  /** Fields that the list can be sorted by */
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      sorter: PropTypes.func,
    })
  ),

  /** Unique ID for this list */
  id: PropTypes.string.isRequired,

  /** Array of IDs (as per `props.dataKey`) of list items to be pinned at the top */
  pinnedIds: PropTypes.arrayOf(PropTypes.string),

  /** Any elements to use as part of the list header (e.g. filtering controls) */
  header: PropTypes.node,

  /** Whether the list is sortable */
  sortable: PropTypes.bool,

  /** The key to sort by if the user hasn't explicitly selected a key */
  defaultSortByKey: PropTypes.string,
};

CardList.defaultProps = {
  header: undefined,
  fields: [],
  pinnedIds: [],
  sortable: false,
};
